import {NodeSelection, Plugin, PluginKey} from 'prosemirror-state';
import {Decoration, DecorationSet} from 'prosemirror-view';
import {widthSize, widthSizeText} from '../helper/grid';
import {findColumn, findGrid} from '../helper/nodeSelection';
import {selectParentNodeOfType} from 'prosemirror-utils';

export const gridColumnResizingKey = new PluginKey('gridColumnResizing');

class ResizeState {
  constructor(activeHandle, dragging) {
    this.activeHandle = activeHandle;
    this.dragging = dragging;
  }

  apply(tr) {
    let state = this, action = tr.getMeta(gridColumnResizingKey);
    if (action && action.setHandle != null) return new ResizeState(
        action.setHandle, null);
    if (action && action.setDragging !== undefined) return new ResizeState(
        state.activeHandle, action.setDragging);
    if (state.activeHandle > -1 && tr.docChanged) {
      state = new ResizeState(null, state.dragging);
    }
    return state;
  }
}

export const gridColumnResizing = ({
                                     handleWidth = 5,
                                     dispatch,
                                   } = {}) => new Plugin({
  key: gridColumnResizingKey, state: {
    init(_, state) {
      return new ResizeState(-1, false);
    }, apply(tr, prev) {
      return prev.apply(tr);
    },
  }, props: {
    attributes(state) {
      let pluginState = gridColumnResizingKey.getState(state);
      return pluginState.activeHandle > -1 ? {class: 'resize-cursor'} : null;
    }, handleDOMEvents: {
      mousemove(view, event) {
        handleMouseMove(view, event, handleWidth);
      }, mouseleave(view) {
        handleMouseLeave(view);
      }, mousedown(view, event) {
        handleMouseDown(view, event);
      },
    }, decorations(state) {
      let pluginState = gridColumnResizingKey.getState(state);
      return handleDecorations(dispatch, state, pluginState.activeHandle);
    },
  },
});

function handleMouseMove(view, event, handleWidth) {
  let pluginState = gridColumnResizingKey.getState(view.state);
  if (!pluginState.dragging) {
    let target = domCellAround(event.target), cell = -1;
    if (target) {
      let {right} = target.getBoundingClientRect();
      if (right - event.clientX <= handleWidth) {
        cell = edgeCell(view, event);
      }

      if (cell !== pluginState.activeHandle) {
        updateHandle(view, cell);
      }
    }
  }
}

function handleMouseLeave(view) {
  let pluginState = gridColumnResizingKey.getState(view.state);
  if (pluginState.activeHandle > -1 && !pluginState.dragging) {
    updateHandle(view, -1);
  }
}

function handleMouseDown(view, event) {
  let pluginState = gridColumnResizingKey.getState(view.state);
  if (pluginState.activeHandle === -1 || pluginState.dragging) {
    return false;
  }

  let cell = view.state.doc.nodeAt(pluginState.activeHandle);
  let width = cell.attrs.width;
  let domWidth = currentColWidth(view, pluginState.activeHandle, cell.attrs);
  let gridWidth = domWidth / (width / 2);
  view.dispatch(view.state.tr.setMeta(gridColumnResizingKey, {
    setDragging: {
      startX: event.clientX,
      startWidth: cell.attrs.width,
      domWidth,
      gridWidth,
    },
  }).
  setSelection(
      new NodeSelection(view.state.doc.resolve(pluginState.activeHandle))));

  function finish(event) {
    window.removeEventListener('mouseup', finish);
    window.removeEventListener('mousemove', move);
    let pluginState = gridColumnResizingKey.getState(view.state);
    if (pluginState.dragging) {
      const {startWidth, gridWidth} = pluginState.dragging;
      const offsetWidth = draggedWidth(pluginState.dragging, event);
      const diffWidth = (Math.floor(offsetWidth / gridWidth) * 2);
      const newWidth = widthSize(startWidth + diffWidth);
      const transaction = view.state.tr.setNodeMarkup(pluginState.activeHandle,
          undefined, {
            ...cell.attrs,
            width: newWidth,
          }).setMeta(gridColumnResizingKey, {setDragging: null});
      view.dispatch(transaction);
    }
  }

  function move(event) {
    if (!event.which) return finish(event);
    let pluginState = gridColumnResizingKey.getState(view.state);
    const {gridWidth, startWidth} = pluginState.dragging;
    const offsetWidth = draggedWidth(pluginState.dragging, event);
    const diffWidth = (Math.floor(offsetWidth / gridWidth) * 2);
    const newWidth = widthSize(startWidth + diffWidth);
    const newWidthText = widthSizeText(newWidth);
    let gridDom = view.domAtPos(pluginState.activeHandle);
    let columnDom = gridDom.node.childNodes[gridDom.offset];
    if (columnDom) {
      const selected = columnDom.classList.contains('selected');
      columnDom.className = `${newWidthText} wide column${cell.attrs.color ?
          ` ${cell.attrs.color}` :
          ''}${cell.attrs.aligned ?
          ` ${cell.attrs.aligned} aligned` :
          ''}${selected ?
          ' selected' :
          ''}`.trim();
    }
  }

  window.addEventListener('mouseup', finish);
  window.addEventListener('mousemove', move);
  event.preventDefault();
  return true;
}

function currentColWidth(view, cellPos) {
  let dom = view.domAtPos(cellPos);
  let node = dom.node.childNodes[dom.offset];
  return node ? node.offsetWidth : 0;
}

function domCellAround(target) {
  while (target && target.nodeName !== 'div' &&
  !target.classList.contains('column')) {
    target = target.classList && target.classList.contains('ProseMirror') ?
        null :
        target.parentNode;
  }
  return target;
}

const cellAround = (state, $pos) => {
  for (let d = $pos.depth - 1; d > 0; d--) {
    if ($pos.node(d).type === state.schema.nodes.grid) {
      return $pos.node(0).resolve($pos.before(d + 1));
    }
  }
  return null;
};

function edgeCell(view, event) {
  const found = view.posAtCoords({left: event.clientX, top: event.clientY});
  if (!found) {
    return -1;
  }

  const $cell = cellAround(view.state, view.state.doc.resolve(found.pos));

  if (!$cell) {
    return -1;
  }

  return $cell.pos;
}

function draggedWidth(dragging, event) {
  let offset = event.clientX - dragging.startX;
  return dragging.startWidth + offset;
}

function updateHandle(view, value) {
  view.dispatch(
      view.state.tr.setMeta(gridColumnResizingKey, {setHandle: value}));
}

function handleDecorations(dispatch, state, cell) {
  const grid = findGrid(state);

  if (!grid) {
    return DecorationSet.empty;
  }

  let decorations = [];

  if (cell > -1) {
    decorations.push(
        Decoration.widget(cell + 1, () => {
          const grip = document.createElement('div');
          grip.className = 'column-resize-handle';
          return grip;
        }),
    );
  }

  const column = findColumn(state);
  if (column) {
    decorations.push(
        Decoration.widget(column.pos + 1, () => {
          const grip = document.createElement('div');
          grip.id = `grip-row-left-${column.pos}`;
          grip.classList.add('grip-row-left');
          grip.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopImmediatePropagation();
            dispatch(selectParentNodeOfType([state.schema.nodes.grid_column])(state.tr));
          });
          return grip;
        }),
        Decoration.widget(column.pos + 1, () => {
          const grip = document.createElement('div');
          grip.id = `grip-row-right-${column.pos}`;
          grip.classList.add('grip-row-right');
          grip.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopImmediatePropagation();
            dispatch(selectParentNodeOfType([state.schema.nodes.grid_column])(state.tr));
          });
          return grip;
        }),
    );
  }

  return DecorationSet.create(state.doc, decorations);
}
